import { Award, Check, Clock, Loader2, TrendingDown } from 'lucide-react';
import { RFQ_QUOTE_STATUS_TONE, formatMinor } from '../constants';
import type { RfqComparison, RfqComparisonRow, SupplierScorecard } from '../types';
import { SupplierScoreBadge } from './SupplierScoreBadge';

/**
 * Side-by-side multi-supplier quotation comparison.
 *
 * Suppliers are COLUMNS and attributes are ROWS, so a buyer reads across one
 * line ("who is fastest?") rather than scanning down five separate cards. The
 * column order, the totals, the lead times and every ranking flag
 * (isLowestPrice / isFastest / isBestValue) come straight from the server's
 * compareRfqQuotes() via GET /api/accounting/rfq/:id — this component ranks
 * nothing itself. Recomputing the comparison in the browser would let the
 * screen and the awarded purchase order disagree about who actually won.
 *
 * Quotes that have not been answered are still rendered (so a buyer can see
 * who has not replied) but are flagged `comparable: false` by the server and
 * are never selectable.
 */

function RankChips({ row }: { row: RfqComparisonRow }) {
  if (!row.comparable) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {row.isLowestPrice && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">
          <TrendingDown className="w-2.5 h-2.5" /> Lowest
        </span>
      )}
      {row.isFastest && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-100 text-sky-700">
          <Clock className="w-2.5 h-2.5" /> Fastest
        </span>
      )}
      {row.isBestValue && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-100 text-violet-700">
          <Award className="w-2.5 h-2.5" /> Best value
        </span>
      )}
    </div>
  );
}

export function RfqComparisonTable({
  comparison,
  scorecards,
  onSelectSupplier,
  selectingSupplierId,
  canSelect,
  locked,
}: {
  comparison: RfqComparison;
  /** Aggregate cards keyed by supplierId, so the buyer sees the rating while choosing. */
  scorecards: Record<string, SupplierScorecard>;
  onSelectSupplier: (row: RfqComparisonRow) => void;
  selectingSupplierId: string | null;
  canSelect: boolean;
  /** Awarded / cancelled / already converted — selection can no longer change. */
  locked: boolean;
}) {
  const rows = comparison.rows || [];

  if (rows.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400 text-sm">
        No suppliers have been added to this RFQ yet.
      </div>
    );
  }

  // A fixed label column plus one column per supplier.
  const labelCls = 'px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap align-top';
  const cellCls = 'px-4 py-3 text-sm align-top border-l border-slate-100 dark:border-slate-800';

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Suppliers asked', value: String(comparison.quoteCount), tone: 'text-slate-800 dark:text-slate-100' },
          { label: 'Quotes received', value: String(comparison.respondedCount), tone: 'text-sky-700' },
          { label: 'Lowest quote', value: formatMinor(comparison.lowestMinor), tone: 'text-emerald-700' },
          { label: 'Spread (highest − lowest)', value: formatMinor(comparison.savingMinor), tone: 'text-amber-700' },
        ].map(card => (
          <div key={card.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3">
            <div className="text-xs text-slate-400">{card.label}</div>
            <div className={`text-lg font-bold mt-0.5 ${card.tone}`}>{card.value}</div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
            <tr>
              <th className={`${labelCls} text-left`}>Supplier</th>
              {rows.map(row => (
                <th
                  key={`${row.supplierId}-${row.quoteIndex}`}
                  className={`${cellCls} text-left ${row.isSelected ? 'bg-emerald-50 dark:bg-emerald-950/30' : ''}`}
                >
                  <div className="font-bold text-slate-800 dark:text-slate-100">
                    {row.supplierName || row.supplierId.slice(-8) || 'Supplier'}
                  </div>
                  <span
                    className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      RFQ_QUOTE_STATUS_TONE[row.status] || RFQ_QUOTE_STATUS_TONE.pending
                    }`}
                  >
                    {row.status}
                  </span>
                  {row.isSelected && (
                    <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white">
                      <Check className="w-2.5 h-2.5" /> Selected
                    </span>
                  )}
                  <RankChips row={row} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
            <tr>
              <td className={labelCls}>Quoted total</td>
              {rows.map(row => (
                <td key={row.quoteIndex} className={`${cellCls} font-bold ${row.isLowestPrice ? 'text-emerald-700' : 'text-slate-800 dark:text-slate-200'}`}>
                  {row.comparable ? formatMinor(row.totalMinor) : <span className="text-slate-300">No response</span>}
                </td>
              ))}
            </tr>
            <tr>
              <td className={labelCls}>Lead time</td>
              {rows.map(row => (
                <td key={row.quoteIndex} className={`${cellCls} ${row.isFastest ? 'text-sky-700 font-semibold' : 'text-slate-600 dark:text-slate-300'}`}>
                  {row.leadTimeDays === null ? '—' : `${row.leadTimeDays} day${row.leadTimeDays === 1 ? '' : 's'}`}
                </td>
              ))}
            </tr>
            <tr>
              <td className={labelCls}>Supplier rating</td>
              {rows.map(row => {
                // Prefer the card fetched for the scorecard strip; fall back to
                // the figures the comparison itself carries, so a rating that
                // exists is never shown as "unrated" just because the extra
                // scorecard request has not resolved yet.
                const card = scorecards[row.supplierId];
                return (
                  <td key={row.quoteIndex} className={cellCls}>
                    <SupplierScoreBadge
                      score={card ? card.overallScore : row.supplierScore}
                      band={card ? card.band : row.supplierScoreBand}
                      ratingCount={card ? card.ratingCount : row.supplierRatingCount}
                    />
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className={labelCls}>Payment terms</td>
              {rows.map(row => (
                <td key={row.quoteIndex} className={`${cellCls} text-slate-600 dark:text-slate-300`}>{row.paymentTerms || '—'}</td>
              ))}
            </tr>
            <tr>
              <td className={labelCls}>Delivery terms</td>
              {rows.map(row => (
                <td key={row.quoteIndex} className={`${cellCls} text-slate-600 dark:text-slate-300`}>{row.deliveryTerms || '—'}</td>
              ))}
            </tr>
            <tr>
              <td className={labelCls}>Warranty</td>
              {rows.map(row => (
                <td key={row.quoteIndex} className={`${cellCls} text-slate-600 dark:text-slate-300`}>{row.warrantyTerms || '—'}</td>
              ))}
            </tr>
            <tr>
              <td className={labelCls}>Remarks</td>
              {rows.map(row => (
                <td key={row.quoteIndex} className={`${cellCls} text-xs text-slate-500`}>{row.remarks || '—'}</td>
              ))}
            </tr>
            <tr>
              <td className={labelCls}>Decision</td>
              {rows.map(row => (
                <td key={row.quoteIndex} className={cellCls}>
                  {row.isSelected ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                      <Check className="w-3.5 h-3.5" /> Selected for PO
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={!row.comparable || !canSelect || locked || selectingSupplierId !== null}
                      onClick={() => onSelectSupplier(row)}
                      title={
                        !row.comparable
                          ? 'This supplier has not submitted a quote'
                          : locked
                            ? 'This RFQ can no longer be changed'
                            : !canSelect
                              ? 'You do not have permission to award an RFQ'
                              : undefined
                      }
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {selectingSupplierId === row.supplierId && <Loader2 className="w-3 h-3 animate-spin" />}
                      Select
                    </button>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default RfqComparisonTable;
